import { FC, useState, useMemo } from "react";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { AiResponseReviewQueryModel } from "@app/operations/ai-response-review/interfaces";
import { AiResponseReviewRating, FilterBoolEnum } from "@app/operations/ai-response-review/enums";
import { getDefaultAiResponseReviewFilters } from "@app/operations/ai-response-review/utils/filterDefaults";
import AiResponseReviewYesNoSelect from "@app/operations/ai-response-review/AiResponseReviewYesNoSelect";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";

interface AiResponseReviewFiltersProps {
  filters: AiResponseReviewQueryModel;
  onFiltersChange: (filters: AiResponseReviewQueryModel) => void;
  onApplyFilters: (filters?: AiResponseReviewQueryModel) => void;
}

const AiResponseReviewFilters: FC<AiResponseReviewFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
}) => {
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);

  const ratingOptions: DropdownItem<AiResponseReviewRating>[] = [
    { key: AiResponseReviewRating.None, name: "None" },
    { key: AiResponseReviewRating.Good, name: "Good" },
    { key: AiResponseReviewRating.Bad, name: "Bad" }
  ];

  const updateFilter = <K extends keyof AiResponseReviewQueryModel>(
    key: K,
    value: AiResponseReviewQueryModel[K]
  ) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleNumericChange = (
    key: keyof AiResponseReviewQueryModel,
    value: string,
    isInteger: boolean = false
  ) => {
    if (value === '') {
      updateFilter(key, undefined);
      return;
    }

    // Allow only valid positive numbers
    const numericPattern = isInteger ? /^\d*$/ : /^\d*\.?\d*$/;
    if (numericPattern.test(value)) {
      const numericValue = isInteger ? parseInt(value) : parseFloat(value);
      if (!isNaN(numericValue)) {
        updateFilter(key, numericValue);
      }
    }
  };

  const handleApplyFilters = () => {
    onApplyFilters();
    setFiltersVisible(false);
  };

  const handleClearFilters = () => {
    onApplyFilters({
      ...getDefaultAiResponseReviewFilters(),
      startDate: undefined
    });
  };

  const handleToggleFiltersMenu = () => {
    setFiltersVisible(!filtersVisible);
  };

  const isValidStartDate = useMemo(() => {
    if ((filters.startDate && !filters.endDate) ||
      (filters.startDate && filters.endDate && filters.startDate < filters.endDate) ||
      (!filters.startDate)) {
      return true;
    }

    return false;
  }, [filters.startDate, filters.endDate]);

  const isValidEndDate = useMemo(() => {
    if ((filters.endDate && !filters.startDate) ||
      (filters.startDate && filters.endDate && filters.endDate > filters.startDate) ||
      (!filters.endDate)) {
      return true;
    }

    return false;
  }, [filters.endDate, filters.startDate]);

  const showFiltersIndicator = useMemo(() => {
    return !!filters.startDate ||
      !!filters.endDate ||
      !!filters.threadId ||
      !!filters.clientId ||
      !!filters.clientName ||
      !!filters.projectId ||
      !!filters.environment ||
      !!filters.userId ||
      !!filters.userName ||
      !!filters.userRating ||
      !!filters.internalRating ||
      filters.minResponseTimeMs !== undefined ||
      !!filters.search ||
      filters.devResolved !== FilterBoolEnum.All ||
      filters.tableGenerated !== FilterBoolEnum.All ||
      filters.tableDownloaded !== FilterBoolEnum.All;
  }, [filters]);

  return (
    <>
      <div className="mb-3 mt-2 ai-response-review-filters">
        <QESButton
          qesType="secondary"
          qesTestId="ai-response-review-filters__toggle"
          onClick={handleToggleFiltersMenu}
          className="col-auto col-xl-auto ai-response-review-filters__toggle"
        >
          {showFiltersIndicator && <span className="qes-button__notification-pip" />}
          Filters
        </QESButton>
        <QESButton
          qesType="secondary"
          qesTestId="ai-response-review-filters__clear"
          onClick={handleClearFilters}
          className="col-auto col-xl-auto"
        >
          Clear All
        </QESButton>
        {filtersVisible && (
          <>
            <div className="ai-response-review-filters__modal-screen"
                 onClick={handleToggleFiltersMenu}></div>
            <div className="ai-response-review-filters__container">
              <div className="ai-response-review-filters__items">
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label ai-response-review-filters__header">Field</div>
                  <div className="ai-response-review-filters__input ai-response-review-filters__header">Value</div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">Start Date</div>
                  <div className="ai-response-review-filters__input">
                    <QESDateInput
                      containerClassName="col-12 col-xl"
                      errorText="Start Date must be before End Date"
                      hideLabel={true}
                      isValid={isValidStartDate}
                      label="Start Date"
                      testId="ai-response-review-filters__start-date"
                      onChange={(e) => updateFilter('startDate', e.target.value ? new Date(e.target.value) : undefined)}
                      unrequiredValidation={true}
                      value={filters.startDate ? filters.startDate.toISOString().split('T')[0] : ""}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">End Date</div>
                  <div className="ai-response-review-filters__input">
                    <QESDateInput
                      containerClassName="col-12 col-xl"
                      errorText="End Date must be after Start Date"
                      hideLabel={true}
                      isValid={isValidEndDate}
                      label="End Date"
                      testId="ai-response-review-filters__end-date"
                      onChange={(e) => updateFilter('endDate', e.target.value ? new Date(e.target.value) : undefined)}
                      unrequiredValidation={true}
                      value={filters.endDate ? filters.endDate.toISOString().split('T')[0] : ""}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">Thread ID</div>
                  <div className="ai-response-review-filters__input">
                    <QESTextInput
                      containerClassName="col-12 col-xl"
                      hideLabel={true}
                      label="Thread ID"
                      testId="ai-response-review-filters__thread-id"
                      value={filters.threadId || ""}
                      onChange={(e) => updateFilter('threadId', e.target.value || undefined)}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">Client ID</div>
                  <div className="ai-response-review-filters__input">
                    <QESTextInput
                      containerClassName="col-12 col-xl"
                      hideLabel={true}
                      type="number"
                      min="0"
                      label="Client ID"
                      testId="ai-response-review-filters__client-id"
                      value={filters.clientId?.toString() || ""}
                      onChange={(e) => handleNumericChange('clientId', e.target.value, true)}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">Client Name</div>
                  <div className="ai-response-review-filters__input">
                    <QESTextInput
                      containerClassName="col-12 col-xl"
                      hideLabel={true}
                      label="Client Name"
                      testId="ai-response-review-filters__client-name"
                      value={filters.clientName || ""}
                      onChange={(e) => updateFilter('clientName', e.target.value || undefined)}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">Project ID</div>
                  <div className="ai-response-review-filters__input">
                    <QESTextInput
                      containerClassName="col-12 col-xl"
                      hideLabel={true}
                      type="number"
                      min="0"
                      label="Project ID"
                      testId="ai-response-review-filters__project-id"
                      value={filters.projectId?.toString() || ""}
                      onChange={(e) => handleNumericChange('projectId', e.target.value, true)}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">Environment</div>
                  <div className="ai-response-review-filters__input">
                    <QESTextInput
                      containerClassName="col-12 col-xl"
                      hideLabel={true}
                      label="Environment"
                      testId="ai-response-review-filters__environment"
                      value={filters.environment || ""}
                      onChange={(e) => updateFilter('environment', e.target.value || undefined)}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">User ID</div>
                  <div className="ai-response-review-filters__input">
                    <QESTextInput
                      containerClassName="col-12 col-xl"
                      hideLabel={true}
                      type="number"
                      min="0"
                      label="User ID"
                      testId="ai-response-review-filters__user-id"
                      value={filters.userId?.toString() || ""}
                      onChange={(e) => handleNumericChange('userId', e.target.value, true)}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">User Name</div>
                  <div className="ai-response-review-filters__input">
                    <QESTextInput
                      containerClassName="col-12 col-xl"
                      hideLabel={true}
                      label="User Name"
                      testId="ai-response-review-filters__user-name"
                      value={filters.userName || ""}
                      onChange={(e) => updateFilter('userName', e.target.value || undefined)}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">User Rating</div>
                  <div className="ai-response-review-filters__input">
                    <QESSelect
                      className="mb-0"
                      containerClassName="mb-0 col-12 col-xl"
                      hideLabel={true}
                      onChange={e => updateFilter('userRating', e.target.value ? e.target.value as AiResponseReviewRating : undefined)}
                      testId="ai-response-review-filters__user-rating"
                      value={filters.userRating || ""}
                      label=""
                    >
                      <option value=""></option>
                      {ratingOptions.map(option => (
                        <option key={option.key} value={option.key}>{option.name}</option>
                      ))}
                    </QESSelect>
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">Internal Rating</div>
                  <div className="ai-response-review-filters__input">
                    <QESSelect
                      className="mb-0"
                      containerClassName="mb-0 col-12 col-xl"
                      hideLabel={true}
                      onChange={e => updateFilter('internalRating', e.target.value ? e.target.value as AiResponseReviewRating : undefined)}
                      testId="ai-response-review-filters__internal-rating"
                      value={filters.internalRating?.toString() || ""}
                      label=""
                    >
                      <option value=""></option>
                      {ratingOptions.map(option => (
                        <option key={option.key} value={option.key}>{option.name}</option>
                      ))}
                    </QESSelect>
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">Min Response Time (ms)</div>
                  <div className="ai-response-review-filters__input">
                    <QESTextInput
                      containerClassName="col-12 col-xl"
                      hideLabel={true}
                      type="number"
                      min="0"
                      label="Min Response Time (ms)"
                      testId="ai-response-review-filters__min-response-time"
                      value={filters.minResponseTimeMs?.toString() || ""}
                      onChange={(e) => handleNumericChange('minResponseTimeMs', e.target.value, true)}
                    />
                  </div>
                </div>
                <div className="ai-response-review-filters__item">
                  <div className="ai-response-review-filters__label">Search (Prompt/Response)</div>
                  <div className="ai-response-review-filters__input">
                    <QESTextInput
                      containerClassName="col-12 col-xl"
                      hideLabel={true}
                      label="Search"
                      testId="ai-response-review-filters__search"
                      value={filters.search || ""}
                      onChange={(e) => updateFilter('search', e.target.value || undefined)}
                    />
                  </div>
                </div>
                <AiResponseReviewYesNoSelect
                  label="Dev Resolved"
                  value={filters.devResolved}
                  updateCallback={(value: FilterBoolEnum) => updateFilter('devResolved', value)}
                  testId="ai-response-review-filters__dev-resolved"
                />
                <AiResponseReviewYesNoSelect
                  label="Table Generated"
                  value={filters.tableGenerated}
                  updateCallback={(value: FilterBoolEnum) => updateFilter('tableGenerated', value)}
                  testId="ai-response-review-filters__table-generated"
                />
                <AiResponseReviewYesNoSelect
                  label="Table Downloaded"
                  value={filters.tableDownloaded}
                  updateCallback={(value: FilterBoolEnum) => updateFilter('tableDownloaded', value)}
                  testId="ai-response-review-filters__table-downloaded"
                />
                <div className="d-flex col-12 col-xl ai-response-review__filter-buttons justify-content-end">
                  <QESButton
                    qesType="primary"
                    qesTestId="ai-response-review-filters__apply"
                    onClick={handleApplyFilters}
                    className="col-auto col-xl-auto"
                  >
                    Apply
                  </QESButton>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AiResponseReviewFilters;
